import React from 'react';
import '../../styles/Skeleton.css';

/**
 * Base Skeleton component - shadcn-style
 */
export const Skeleton = ({ className = '', style = {}, ...props }) => {
  return (
    <div
      className={`skeleton ${className}`}
      style={style}
      {...props}
    />
  );
};

/**
 * Skeleton for conversation list items
 */
export const ConversationSkeleton = () => {
  return (
    <div className="conversation-skeleton">
      <Skeleton className="skeleton-avatar" />
      <div className="skeleton-content">
        <div className="skeleton-header">
          <Skeleton className="skeleton-name" />
          <Skeleton className="skeleton-time" />
        </div>
        <Skeleton className="skeleton-preview" />
      </div>
    </div>
  );
};

/**
 * Skeleton for product cards
 */
export const ProductCardSkeleton = () => {
  return (
    <div className="product-card-skeleton">
      <div className="skeleton-card-content">
        <Skeleton className="skeleton-title" style={{ width: '50%', height: '20px', marginBottom: '0.5rem' }} />
        <Skeleton className="skeleton-text" style={{ width: '100%', height: '14px', marginBottom: '0.25rem' }} />
        <Skeleton className="skeleton-text" style={{ width: '80%', height: '14px', marginBottom: '0.75rem' }} />
        <Skeleton className="skeleton-image" />
        <div className="skeleton-stats" style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', marginBottom: '0.75rem' }}>
          <Skeleton style={{ width: 60, height: 16 }} />
          <Skeleton style={{ width: 80, height: 16 }} />
        </div>
        <div className="skeleton-footer">
          <Skeleton className="skeleton-price" />
          <Skeleton className="skeleton-button" />
        </div>
        <div className="skeleton-seller" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)' }}>
          <Skeleton style={{ width: 24, height: 24, borderRadius: '50%' }} />
          <Skeleton style={{ width: 80, height: 14 }} />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for message bubbles
 */
export const MessageSkeleton = ({ isOwn = false }) => {
  return (
    <div className={`message-skeleton ${isOwn ? 'own' : 'other'}`}>
      {!isOwn && <Skeleton className="skeleton-avatar-small" />}
      <div className="skeleton-message-content">
        <Skeleton className="skeleton-message-bubble" style={{ width: `${Math.random() * 40 + 40}%` }} />
      </div>
    </div>
  );
};

/**
 * Skeleton for product detail page
 */
export const ProductDetailSkeleton = () => {
  return (
    <div className="product-detail-skeleton">
      <div className="skeleton-detail-left">
        <Skeleton className="skeleton-detail-image" />
        <div className="skeleton-detail-thumbnails">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="skeleton-thumbnail" />
          ))}
        </div>
      </div>
      <div className="skeleton-detail-right">
        <Skeleton className="skeleton-detail-title" />
        <Skeleton className="skeleton-detail-price" />
        <Skeleton className="skeleton-detail-description" />
        <Skeleton className="skeleton-detail-description" style={{ width: '80%' }} />
        <div className="skeleton-detail-specs">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="skeleton-spec" />
          ))}
        </div>
        <Skeleton className="skeleton-detail-button" />
      </div>
    </div>
  );
};

/**
 * Skeleton for table rows
 */
export const TableRowSkeleton = ({ columns = 4 }) => {
  return (
    <tr className="table-row-skeleton">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i}>
          <Skeleton className="skeleton-table-cell" />
        </td>
      ))}
    </tr>
  );
};

/**
 * Skeleton for notification items
 */
export const NotificationSkeleton = () => {
  return (
    <div className="notification-skeleton">
      <Skeleton className="skeleton-notification-icon" />
      <div className="skeleton-notification-content">
        <div className="skeleton-notification-header">
          <Skeleton className="skeleton-notification-title" />
          <Skeleton className="skeleton-notification-button" />
        </div>
        <Skeleton className="skeleton-notification-message" />
        <Skeleton className="skeleton-notification-time" />
      </div>
    </div>
  );
};

/**
 * Skeleton for top-ups page - matches actual layout structure
 */
export const TopUpsSkeleton = ({ hasPlatforms = false, hasRegions = false }) => {
  return (
    <div className="topups-content" style={{ paddingTop: '118px' }}>
      <div className="topups-container">
        <div className="topups-layout">
          {/* Left Column */}
          <div className="topups-left">
            <div className="select-amount-section">
              {/* Platform selector skeleton */}
              {hasPlatforms && (
                <div className="platform-selector-inline" style={{ marginBottom: '1.5rem' }}>
                  <Skeleton width="140px" height="20px" style={{ marginBottom: '0.75rem', borderRadius: '4px' }} />
                  <div className="platform-buttons" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4].map(i => (
                      <Skeleton key={i} width="90px" height="36px" style={{ borderRadius: '6px' }} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Region selector skeleton */}
              {hasRegions && (
                <div className="platform-selector-inline" style={{ marginBottom: '1.5rem' }}>
                  <Skeleton width="140px" height="20px" style={{ marginBottom: '0.75rem', borderRadius: '4px' }} />
                  <div className="platform-buttons" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} width="70px" height="36px" style={{ borderRadius: '6px' }} />
                    ))}
                  </div>
                </div>
              )}
              
              <Skeleton width="120px" height="20px" style={{ marginTop: hasPlatforms || hasRegions ? '1.5rem' : '0', marginBottom: '1rem', borderRadius: '4px' }} />
              
              {/* Amount grid skeleton - matches actual card structure with proper layout */}
              <div className="amount-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                  <div key={i} className="amount-card" style={{ 
                    backgroundColor: 'var(--bg-quaternary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.75rem',
                    position: 'relative'
                  }}>
                    <Skeleton width="48px" height="48px" style={{ borderRadius: '8px' }} />
                    <div className="amount-info" style={{ width: '100%', textAlign: 'center' }}>
                      <Skeleton width="70px" height="16px" style={{ margin: '0 auto 0.5rem', borderRadius: '4px' }} />
                      <Skeleton width="50px" height="14px" style={{ margin: '0 auto', borderRadius: '4px' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary Skeleton - matches actual structure */}
          <div className="topups-right">
            <div className="order-summary" style={{ 
              backgroundColor: 'var(--bg-quaternary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              {/* Selected item display skeleton */}
              <div className="selected-item-display" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                marginBottom: '1.5rem',
                paddingBottom: '1.5rem',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <Skeleton width="48px" height="48px" style={{ borderRadius: '8px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton width="120px" height="18px" style={{ marginBottom: '0.5rem', borderRadius: '4px' }} />
                  <Skeleton width="100px" height="14px" style={{ borderRadius: '4px' }} />
                </div>
              </div>

              {/* Total price skeleton */}
              <Skeleton width="100px" height="24px" style={{ marginBottom: '1.5rem', borderRadius: '4px' }} />

              {/* Buy button skeleton */}
              <Skeleton width="100%" height="48px" style={{ borderRadius: '6px', marginBottom: '1rem' }} />

              {/* Guarantees section skeleton */}
              <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Skeleton width="16px" height="16px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                  <Skeleton width="200px" height="14px" style={{ borderRadius: '4px' }} />
                </div>
              </div>

              {/* Delivery instructions skeleton */}
              <div style={{ marginBottom: '1.5rem' }}>
                <Skeleton width="150px" height="18px" style={{ marginBottom: '1rem', borderRadius: '4px' }} />
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Skeleton width="14px" height="14px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                    <Skeleton width={`${120 + i * 20}px`} height="14px" style={{ borderRadius: '4px' }} />
                  </div>
                ))}
              </div>

              {/* Seller info card skeleton */}
              <div style={{ 
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '6px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Skeleton width="32px" height="32px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="100px" height="16px" style={{ marginBottom: '0.25rem', borderRadius: '4px' }} />
                    <Skeleton width="80px" height="14px" style={{ borderRadius: '4px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for the shared boosting request page (hero, spec grid, offer list, side panel)
 */
export const BoostingRequestSkeleton = () => {
  return (
    <div className="boosting-request-skeleton">
      <Skeleton className="brs-crumb" />

      <div className="brs-hero">
        <Skeleton className="brs-hero-icon" />
        <div className="brs-hero-text">
          <Skeleton className="brs-hero-kicker" />
          <Skeleton className="brs-hero-title" />
          <Skeleton className="brs-hero-meta" />
        </div>
        <Skeleton className="brs-hero-action" />
      </div>

      <div className="brs-stats">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="brs-stat" />
        ))}
      </div>

      <div className="brs-grid">
        <div className="brs-col">
          <div className="brs-card">
            <Skeleton className="brs-card-title" />
            <div className="brs-specs">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="brs-spec">
                  <Skeleton className="brs-spec-label" />
                  <Skeleton className="brs-spec-value" />
                </div>
              ))}
            </div>
          </div>

          <div className="brs-card">
            <Skeleton className="brs-card-title" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="brs-offer">
                <Skeleton className="brs-offer-avatar" />
                <div className="brs-offer-text">
                  <Skeleton className="brs-offer-name" />
                  <Skeleton className="brs-offer-sub" />
                </div>
                <Skeleton className="brs-offer-price" />
                <Skeleton className="brs-offer-btn" />
              </div>
            ))}
          </div>
        </div>

        <div className="brs-col">
          <div className="brs-card">
            <Skeleton className="brs-card-title" />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="brs-side-row" />
            ))}
            <Skeleton className="brs-side-btn" />
          </div>
        </div>
      </div>

      <div className="brs-card brs-chat">
        <Skeleton className="brs-card-title" />
        <div className="brs-chat-panel">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`brs-chat-msg${i % 2 === 0 ? ' is-own' : ''}`}>
              <Skeleton className="brs-chat-bubble" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const BoostingCheckoutSkeleton = () => {
  return (
    <div className="boosting-checkout-skeleton">
      <Skeleton className="bcs-title" />

      <div className="bcs-grid">
        <div className="bcs-card">
          <Skeleton className="bcs-card-title" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="bcs-line">
              <Skeleton className="bcs-line-label" />
              <Skeleton className="bcs-line-value" />
            </div>
          ))}
          <Skeleton className="bcs-total" />
        </div>

        <div className="bcs-card">
          <Skeleton className="bcs-card-title" />
          <Skeleton className="bcs-balance" />
          <Skeleton className="bcs-btn" />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;

